import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Bell, Loader2, CheckCircle2, XCircle, RefreshCw,
} from 'lucide-react';
import { listStaleESignOffers, remindStaleESignOffers } from '../api';

function relTime(iso) {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const hrs = Math.floor(diff / 3_600_000);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch { return iso; }
}

export default function ZohoSignRemindStaleDialog({ open, onOpenChange, onComplete }) {
  const [threshold, setThreshold] = useState(24);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(async (hours = threshold) => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await listStaleESignOffers(hours);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load stale offers');
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => {
    if (open) loadPreview(threshold);
  }, [open, loadPreview, threshold]);

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await remindStaleESignOffers({
        stale_threshold_hours: Number(threshold),
        dry_run: false,
      });
      setResult(data);
      if (data.reminded > 0) {
        toast.success(`Reminders sent for ${data.reminded} stale request(s)`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} reminder(s) failed`, { duration: 6000 });
      }
      if (data.reminded === 0 && data.failed === 0) {
        toast.info('No stale requests to remind');
      }
      if (onComplete) onComplete(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk reminder failed');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    setPreview(null);
    setResult(null);
    onOpenChange(false);
  };

  const display = result || preview;
  const items = display?.results || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !sending && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="remind-stale-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell size={20} className="text-amber-600" />
            Remind Stale E-Sign Requests
          </DialogTitle>
          <DialogDescription>
            Find offers still in <Badge className="bg-blue-50 text-blue-700 border-blue-200">SENT</Badge>
            {' or '}
            <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">VIEWED</Badge>
            {' '}state without activity for a while, and send reminders via Zoho Sign in one click.
          </DialogDescription>
        </DialogHeader>

        {/* Threshold selector */}
        <div className="flex items-end gap-3 border-b pb-4">
          <div className="flex-1">
            <Label htmlFor="stale-hours">Consider stale if no activity for (hours)</Label>
            <Input
              id="stale-hours"
              type="number"
              min="1"
              max="168"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              disabled={loading || sending}
              data-testid="stale-threshold"
            />
            <p className="text-xs text-slate-500 mt-1">
              Common values: 24h (next day), 48h (2 days), 72h (3 days), 168h (1 week)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadPreview(threshold)}
            disabled={loading || sending}
            data-testid="stale-reload"
          >
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Stale" value={display?.total_stale ?? '—'} color="text-amber-600" testid="stale-count" />
          <Stat label="Reminded" value={result?.reminded ?? 0} color="text-emerald-600" testid="reminded-count" />
          <Stat label="Failed" value={result?.failed ?? 0} color="text-red-600" testid="failed-count" />
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="animate-spin mr-2" size={18} />
            Loading stale requests...
          </div>
        )}

        {/* Empty */}
        {!loading && display && display.total_stale === 0 && (
          <div className="text-center py-8 text-slate-500 text-sm">
            🎉 No stale e-sign requests! Everything is fresh.
          </div>
        )}

        {/* Results list */}
        {!loading && items.length > 0 && (
          <div className="max-h-80 overflow-y-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-left p-2 font-medium">Candidate</th>
                  <th className="text-left p-2 font-medium">Email</th>
                  <th className="text-left p-2 font-medium">State</th>
                  <th className="text-left p-2 font-medium">Last activity</th>
                  <th className="text-left p-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => (
                  <tr key={r.offer_id || idx} className="border-t" data-testid={`stale-row-${idx}`}>
                    <td className="p-2">
                      {result == null ? (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                          Pending
                        </Badge>
                      ) : r.success ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                          <CheckCircle2 size={11} /> Reminded
                        </Badge>
                      ) : r.skipped ? (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200">Skipped</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
                          <XCircle size={11} /> Failed
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 font-medium">{r.candidate_name || '—'}</td>
                    <td className="p-2 text-slate-600">{r.recipient_email || '—'}</td>
                    <td className="p-2">
                      <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
                    </td>
                    <td className="p-2 text-slate-600">
                      <div>{relTime(r.last_event_at)}</div>
                      {r.hours_stale != null && (
                        <div className="text-[10px] text-slate-400">{r.hours_stale}h</div>
                      )}
                    </td>
                    <td className="p-2 text-slate-500 max-w-[200px] truncate">
                      {result && r.reason && r.reason !== 'dry_run' ? r.reason : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={sending} data-testid="stale-close">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleSend}
              disabled={sending || loading || !display || display.total_stale === 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="stale-remind-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="animate-spin mr-1.5" size={16} />
                  Sending {display?.total_stale ?? 0} reminders...
                </>
              ) : (
                <>
                  <Bell size={16} className="mr-1.5" />
                  Remind {display?.total_stale ?? 0} now
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color = 'text-slate-900', testid }) {
  return (
    <div className="rounded-lg border bg-slate-50 px-3 py-2 text-center">
      <div className={`text-2xl font-bold ${color}`} data-testid={testid}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
